import { NewCommitmentServerResponse } from "@data/types";
import { Commitment, CommitmentServer, useCommitmentStore } from "@mod/commitment";
import { commitmentServerSorter } from "@mod/commitment/lib/servers-sorter";
import { Group, useGroupStore } from "@mod/groups";
import { useParams } from "react-router-dom";
import { useApi } from "./api";
import {
  apiRoutes,
  COMMITMENT_DASHBOARD_DATA_QUERY_KEY,
  COMMITMENT_GUESTS_QUERY_KEY,
  COMMITMENT_LIST_QUERY_KEY,
  COMMITMENT_PUBLIC_GROUP_QUERY_KEY,
  COMMITMENT_PUBLIC_GUEST_REGISTRATION_MUTATION,
  COMMITMENT_PUBLIC_QUERY_KEY,
  COMMITMENT_PUBLIC_REGISTRATION_MUTATION,
  COMMITMENT_PUBLIC_VERIFY_SERVER_BY_PHONE_QUERY_KEY,
  COMMITMENT_SERVER_ATTENDANCE_MUTATION,
  COMMITMENT_SERVER_PAYMENT_UPDATE_MUTATION,
  COMMITMENT_SERVER_REMOVE_MUTATION,
  COMMITMENT_SERVER_UPDATE_MUTATION,
  COMMITMENT_SERVER_UPLOAD_PROFILE_PICTURE_MUTATION,
  COMMITMENT_SERVERS_BY_GROUP_PUBLIC_QUERY_KEY,
  COMMITMENT_SERVERS_QUERY_KEY,
  CREATE_SERVICE_SERVER_MUTATION,
  CURRENT_COMMITMENT_QUERY_KEY,
  queryClient
} from "./lib";

import { filterServer } from "@data/lib/filter-server";
import type {
  CommitmentInscriptionByGroup,
  CommitmentServersDashboardData,
  ResponseType,
  UploadPaymentResponse
} from "@data/types";
import { getBase64 } from "@mod/core/utils/get-base64";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";

export const useCommitmentPublicGroupData = () => {
  const { groupCode } = useParams();

  const { data: group, isFetching: groupIsFetching } = useApi(
    COMMITMENT_PUBLIC_GROUP_QUERY_KEY
  ).useSecuredApiCall<Group>({
    url: apiRoutes.commitmentGroupByCode(groupCode ?? ""),
    keys: [COMMITMENT_PUBLIC_GROUP_QUERY_KEY, groupCode],
    enabled: !!groupCode
  });

  return { group: group?.data, loading: groupIsFetching };
};

export const useCommitmentInscriptionData = () => {
  const { commitmentHash } = useParams();

  const { data: commitment, isFetching: commitmentIsFetching } = useApi(
    COMMITMENT_PUBLIC_QUERY_KEY
  ).useSecuredApiCall<Commitment>({
    url: apiRoutes.commmitmentByHash(commitmentHash ?? ""),
    keys: [COMMITMENT_PUBLIC_QUERY_KEY, commitmentHash],
    enabled: !!commitmentHash
  });

  return { commitment: commitment?.data, loading: commitmentIsFetching };
};

export const useCommitmentInscription = () => {
  const {
    mutateAsync: registerToCommitment,
    isPending: isRegistering,
    data: registerToCommitmentResponse,
    error: registerToCommitmentError
  } = useApi(COMMITMENT_PUBLIC_REGISTRATION_MUTATION).useApiMutation<NewCommitmentServerResponse>({
    url: apiRoutes.commitmentRegister,
    method: "POST"
  });

  return { registerToCommitment, isRegistering, registerToCommitmentResponse, registerToCommitmentError };
};

export const useCreateServiceServer = () => {
  const {
    mutateAsync: createServiceServer,
    isPending: isCreatingServer,
    data: createServiceServerResponse,
    error: createServiceServerError
  } = useApi(CREATE_SERVICE_SERVER_MUTATION).useApiMutation<NewCommitmentServerResponse>({
    url: apiRoutes.createServiceServer,
    method: "POST"
  });

  return { createServiceServer, isCreatingServer, createServiceServerResponse, createServiceServerError };
};

export const useCommitmentGuestInscription = () => {
  const {
    mutateAsync: registerToCommitment,
    isPending: isRegistering,
    data: registerToCommitmentResponse,
    error: registerToCommitmentError
  } = useApi(COMMITMENT_PUBLIC_GUEST_REGISTRATION_MUTATION).useApiMutation<NewCommitmentServerResponse>({
    url: apiRoutes.commitmentGuestRegister,
    method: "POST"
  });

  return { registerToCommitment, isRegistering, registerToCommitmentResponse, registerToCommitmentError };
};

export const useCommitmentVerifyServerByPhone = () => {
  const {
    mutateAsync: verifyServerByPhone,
    isPending: isVerifying,
    data: verifyServerResponse,
    error: verifyServerError
  } = useApi(COMMITMENT_PUBLIC_VERIFY_SERVER_BY_PHONE_QUERY_KEY).useApiMutation<ResponseType<boolean>>({
    url: apiRoutes.commitmentVerifyServerByPhone,
    method: "POST"
  });

  return { verifyServerByPhone, isVerifying, verifyServerResponse, verifyServerError };
};

export const useCommitmentList = () => {
  const { data: commitments, isFetching: isCommitmentsFetching } = useApi(COMMITMENT_LIST_QUERY_KEY).useSecuredApiCall<
    Commitment[]
  >({ url: apiRoutes.commitmentList, keys: [COMMITMENT_LIST_QUERY_KEY], enabled: true });

  return { commitments, isCommitmentsFetching };
};

export const useCurrentCommitment = () => {
  const { data: commitment, isFetching: isCommitmentsFetching } = useApi(
    CURRENT_COMMITMENT_QUERY_KEY
  ).useSecuredApiCall<Commitment>({
    url: apiRoutes.currentCommitment,
    keys: [CURRENT_COMMITMENT_QUERY_KEY],
    enabled: true
  });

  return { commitment: commitment?.data, isCommitmentsFetching };
};

/** A commitment server is a preacher when its ministry is "Prédica" (accents and case ignored). */
export const isPreacherServer = (server: CommitmentServer) =>
  (server.minister || "").toUpperCase().normalize("NFD").replace(/[̀-ͯ]/g, "").includes("PREDICA");

export const useCommitmentServers = (forceViewAll?: boolean) => {
  const { groupSelected, viewAllGroups } = useGroupStore();
  const { commitment } = useCurrentCommitment();
  const { attendanceFilters, searchText, selectCommitment } = useCommitmentStore();

  const groupId = viewAllGroups || forceViewAll ? "all" : (groupSelected?.id ?? "");

  const commitmentId = commitment?.id ?? "";

  const {
    data: commitmentsServers,
    isLoading, // first load
    isFetching // subsequent loads
  } = useApi(COMMITMENT_SERVERS_QUERY_KEY).useSecuredApiCall<CommitmentServer[]>({
    url: apiRoutes.commitmentServers(commitmentId, groupId),
    keys: { commitmentId, groupId },
    enabled: !!commitmentId && !!groupId
  });

  const serverFixedIds =
    commitmentsServers?.data
      ?.map((server) => ({ ...server, id: server._id ?? server.id }))
      .filter((server) => !!server.id) ?? [];

  if (serverFixedIds) serverFixedIds.sort(commitmentServerSorter);

  const commitmentsFiltered = serverFixedIds?.filter(filterServer(searchText)) ?? [];

  // sort preachers alphabetically
  const preacherSorter = (a: CommitmentServer, b: CommitmentServer) => {
    const ministerA = a.firstName ? a.firstName : a.minister || "";
    const ministerB = b.firstName ? b.firstName : b.minister || "";
    return ministerA.localeCompare(ministerB);
  };

  const preachersBase = commitmentsFiltered.filter(isPreacherServer).sort(preacherSorter);

  // Same preachers ignoring `searchText`: it is global and persisted in sessionStorage, so a search
  // typed on another screen would otherwise keep hiding preachers in the preaching module.
  const allPreachers = serverFixedIds.filter(isPreacherServer).sort(preacherSorter);

  useEffect(() => {
    if (commitment) {
      selectCommitment(commitment);
    }
  }, [commitment]);

  const commitmentsFilteredByAttndance = useMemo(
    () =>
      commitmentsFiltered.filter((server) => {
        if (attendanceFilters.pending && attendanceFilters.attended) return true;

        if (attendanceFilters.pending) return !server.attendance;

        if (attendanceFilters.attended) return server.attendance;

        return true;
      }),
    [attendanceFilters, commitmentsFiltered]
  );

  return {
    commitmentsServers: serverFixedIds,
    commitmentsFiltered,
    commitmentsFilteredByAttndance,
    preachersBase,
    allPreachers,
    isLoading,
    isFetching
  };
};

export const useCommitmentInscriptionByGroup = () => {
  const { commitmentHash, groupCode } = useParams();

  const { data, isFetching, isLoading } = useApi(
    COMMITMENT_SERVERS_BY_GROUP_PUBLIC_QUERY_KEY
  ).useSecuredApiCall<CommitmentInscriptionByGroup>({
    url: apiRoutes.commitmentInscriptionByGroup(commitmentHash ?? "", groupCode ?? ""),
    keys: { commitmentHash, groupCode },
    enabled: !!commitmentHash && !!groupCode
  });

  const { commitment } = data?.data ?? {};

  const isDone = dayjs().isAfter(dayjs(commitment?.endDate, "YYYY-MM-DD"), "day");

  return { data: data?.data, isFetching, isLoading, isDone };
};

export const useCommitmentDashboardData = () => {
  const { commitmentSelected } = useCommitmentStore();
  const { groupSelected, viewAllGroups } = useGroupStore();

  const groupId = viewAllGroups ? "all" : (groupSelected?.id ?? "");

  const { data, isFetching, isLoading } = useApi(
    COMMITMENT_DASHBOARD_DATA_QUERY_KEY
  ).useSecuredApiCall<CommitmentServersDashboardData>({
    url: apiRoutes.commitmentDashboard,
    method: "POST",
    payload: { commitmentId: commitmentSelected?.id, groupId: !viewAllGroups ? groupSelected?.id : undefined },
    keys: { commitmentId: commitmentSelected?.id, groupId },
    enabled: !!commitmentSelected?.id
  });

  return { data: data?.data, isFetching, isLoading };
};

export const useCommitmentServerUpdate = () => {
  const { commitmentSelected } = useCommitmentStore();

  const { mutateAsync, isPending, data } = useApi(
    COMMITMENT_SERVER_UPDATE_MUTATION
  ).useApiMutation<NewCommitmentServerResponse>({
    url: apiRoutes.commitmentServerUpdate,
    method: "PUT",
    onSuccess: (response, newData) => {
      if (!response.success) return;

      updateCommitmentServerCache(
        {
          ...newData
        },
        commitmentSelected?.id ?? "",
        newData?.groupId ?? ""
      );
    }
  });

  return { update: mutateAsync, loading: isPending, response: data?.data ?? null };
};

export const useCommitmentServerAttendanceUpdate = () => {
  const { mutateAsync, isPending, data } = useApi(
    COMMITMENT_SERVER_ATTENDANCE_MUTATION
  ).useApiMutation<NewCommitmentServerResponse>({ url: apiRoutes.commitmentServerAttendanceUpdate, method: "PUT" });

  return { update: mutateAsync, loading: isPending, response: data?.data ?? null };
};

export const updateCommitmentServerCache = (
  props: Partial<CommitmentServer>,
  commitmentId: string,
  groupId: string
) => {
  queryClient.setQueryData([COMMITMENT_SERVERS_QUERY_KEY, { commitmentId, groupId }], (oldData: any) => {
    if (!oldData) return oldData;

    return {
      ...oldData,
      data: oldData.data.map((server: CommitmentServer) => {
        if (server.id === props.id) {
          return { ...server, ...props };
        }

        return server;
      })
    };
  });
  queryClient.setQueryData([COMMITMENT_GUESTS_QUERY_KEY, { commitmentId }], (oldData: any) => {
    if (!oldData) return oldData;

    return {
      ...oldData,
      data: oldData.data.map((server: CommitmentServer) => {
        if (server.id === props.id) {
          return { ...server, ...props };
        }

        return server;
      })
    };
  });
};

export const updateCommitmentPublicServerCache = (props: Partial<CommitmentServer>, keys: any) => {
  queryClient.setQueryData([COMMITMENT_SERVERS_BY_GROUP_PUBLIC_QUERY_KEY, keys], (oldData: any) => {
    if (!oldData) return oldData;

    return {
      ...oldData,
      data: {
        ...oldData.data,
        servers: oldData.data.servers.map((server: CommitmentServer) => {
          if (server.id === props.id) {
            return { ...server, ...props };
          }

          return server;
        })
      }
    };
  });
};

export const removeServerFromCache = (id: string, key: any) => {
  queryClient.setQueryData([COMMITMENT_SERVERS_QUERY_KEY, key], (oldData: any) => {
    return { ...oldData, data: oldData.data.filter((server: CommitmentServer) => server.id !== id) };
  });
  queryClient.setQueryData([COMMITMENT_GUESTS_QUERY_KEY, { commitmentId: key.commitmentId }], (oldData: any) => {
    return { ...oldData, data: oldData.data.filter((server: CommitmentServer) => server.id !== id) };
  });

  queryClient.invalidateQueries({ queryKey: [COMMITMENT_DASHBOARD_DATA_QUERY_KEY, key] });
};

export const useUploadServerPayment = () => {
  const { serverSelected, selectServer, commitmentSelected } = useCommitmentStore();

  const {
    mutateAsync: uploadPaymentMutation,
    isPending: loadingUploadPayment,
    data: responseUploadPayment
  } = useApi(COMMITMENT_SERVER_PAYMENT_UPDATE_MUTATION).useApiMutation<UploadPaymentResponse>({
    url: apiRoutes.commitmentServerPayment,
    method: "POST",
    keys: { id: serverSelected?.id, commitmentId: commitmentSelected?.id },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMMITMENT_SERVERS_QUERY_KEY] });
      selectServer(null);
    }
  });

  const uploadPaymentWithFile = async ({
    data,
    fileName,
    format,
    amount,
    id,
    type,
    comments,
    isRefund
  }: {
    data?: File | null;
    fileName?: string;
    format?: string;
    amount: number;
    id: string | string[];
    type: string;
    comments: string;
    isRefund?: boolean;
  }) => {
    const base64String = data ? ((await getBase64(data)) as string) : "";

    const base64EncodedImageString = base64String.replace(/^data:image\/\w+;base64,/, "");
    const base64EncodedPdfeString = base64String.replace("data:application/pdf;base64,", "");

    const fileBase64 = format?.includes("pdf") ? base64EncodedPdfeString : base64EncodedImageString;

    return await uploadPaymentMutation({
      base64ImageString: fileBase64,
      fileName,
      format,
      commitmentServerId: id,
      commitmentId: commitmentSelected?.id,
      amount,
      type: type,
      comments: comments ?? "",
      isRefund
    });
  };

  return { uploadPaymentWithFile, loadingUploadPayment, responseUploadPayment };
};

export const useRemoveCommitmentServer = () => {
  const { mutateAsync, isPending, data } = useApi(
    COMMITMENT_SERVER_REMOVE_MUTATION
  ).useApiMutation<NewCommitmentServerResponse>({
    url: apiRoutes.commitmentServerRemove,
    method: "DELETE",
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMMITMENT_SERVERS_QUERY_KEY] });
    }
  });

  return { remove: mutateAsync, loading: isPending, response: data?.data ?? null };
};

export const useUploadServerProfilePicture = () => {
  const {
    mutateAsync: uploadProfilePictureMutation,
    isPending: isUploading,
    data: uploadResponse
  } = useApi(COMMITMENT_SERVER_UPLOAD_PROFILE_PICTURE_MUTATION).useApiMutation<
    ResponseType<{ profilePictureUrl: string }>
  >({
    url: apiRoutes.commitmentServerUploadProfilePicture,
    method: "PUT",
    onSuccess: (response, variables) => {
      const profilePictureUrl = (response as any)?.data?.profilePictureUrl ?? (response as any)?.profilePictureUrl;
      if (profilePictureUrl && variables?.id) {
        queryClient.setQueriesData({ queryKey: [COMMITMENT_SERVERS_QUERY_KEY] }, (oldData: any) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((server: CommitmentServer) =>
              (server.id ?? server._id) === variables.id ? { ...server, profilePictureUrl } : server
            )
          };
        });
      }
      queryClient.invalidateQueries({ queryKey: [COMMITMENT_SERVERS_QUERY_KEY] });
    }
  });

  const uploadProfilePicture = async (id: string, file: File) => {
    const base64String = (await getBase64(file)) as string;
    const base64ImageString = base64String.replace(/^data:image\/\w+;base64,/, "");

    return await uploadProfilePictureMutation({
      id,
      base64ImageString,
      fileName: file.name
    });
  };

  return { uploadProfilePicture, isUploading, uploadResponse };
};
